import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import PartPicker from 'components/PartPicker'
import { EUI_FORM_MAX_WIDTH } from 'components/euiParity'
import { euiIconTypeMap } from 'components/icons'
import { Part } from '../../../../../graphql'

const SwapIcon = euiIconTypeMap.merge

export type MergeProductsFormProps = {
	fromPart?: Part,
	onCancel: () => void,
	onSuccess: () => void,
	onSubmit: ( goodPartId: number, dupePartId: number ) => Promise<boolean>,
}

export default function MergeProductsForm( { fromPart, onCancel, onSuccess, onSubmit }: MergeProductsFormProps ): JSX.Element {
	const [ goodPart, setGoodPart ] = useState<Part | undefined>( fromPart )
	const [ duplicatePart, setDuplicatePart ] = useState<Part | undefined>()
	const [ isSaving, setIsSaving ] = useState( false )

	const handleSwapParts = () => {
		setDuplicatePart( goodPart )
		setGoodPart( duplicatePart )
	}

	const handleSubmit = async() => {
		if ( !goodPart || !duplicatePart ) {
			return
		}
		setIsSaving( true )
		let merged = false
		try {
			merged = await onSubmit( goodPart.id, duplicatePart.id )
		} catch {
			// The Apollo error link has already toasted the failure
		}
		if ( !merged ) {
			// Stay open so the user can retry
			setIsSaving( false )
			return
		}
		onSuccess()
	}

	const duplicateHasVendorInfo = !!duplicatePart?.vendors?.length

	return (
		<>
			<DialogContent>
				{/* Capped like the pickers so the warning wraps instead of stretching the dialog. */}
				<Stack spacing={2} sx={{ maxWidth: EUI_FORM_MAX_WIDTH }}>
					<PartPicker
						id="goodPart"
						label="Good Product"
						onChange={( _, part ) => setGoodPart( part )}
						selectedPart={goodPart}
						required
					/>
					<IconButton aria-label="Swap Products" title="Swap Products" onClick={handleSwapParts} sx={{ alignSelf: 'flex-start' }}>
						<SwapIcon />
					</IconButton>
					<PartPicker
						id="duplicatePart"
						label="Duplicate Product"
						onChange={( _, part ) => setDuplicatePart( part )}
						selectedPart={duplicatePart}
						required
					/>
					<Alert severity="error">
						<Stack spacing={1}>
							<Typography variant="body2">
								The duplicate product will be removed, and the good product selected above will take over its assignments.
							</Typography>
							{ duplicateHasVendorInfo && (
								<Typography variant="body2">
									All vendor information on the duplicate product will be discarded.
								</Typography>
							) }
							<Typography variant="subtitle2">
								This action cannot be undone!
							</Typography>
						</Stack>
					</Alert>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button color="inherit" onClick={onCancel}>
					Cancel
				</Button>
				<Button
					variant="contained"
					disabled={!goodPart || !duplicatePart}
					loading={isSaving}
					onClick={handleSubmit}
				>
					Submit
				</Button>
			</DialogActions>
		</>
	)
}
